import { useEffect, useState } from 'react';
import { useStore } from '@nanostores/react';
import { Banner, Button, Center, LinkProvider, Spinner } from '@astryxdesign/core';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router';

import { AppLink } from './AppLink';
import { AppHeader, SiteFooter, SiteHeader } from './SiteChrome';
import { useDocumentMeta } from '../seo/useDocumentMeta';
import { $auth } from '../stores/auth';
import { api } from '../lib/api';

/**
 * Root layout. Sits inside the router so `AppLink` can use router context, and
 * installs it for every Astryx component below.
 */
export function RootLayout() {
  const { pathname } = useLocation();

  // Title, description, canonical and og:* tags follow the route — the server
  // sent the ones for the URL that was loaded, this keeps them right afterwards.
  useDocumentMeta();

  // Route changes should start at the top of the new page.
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [pathname]);

  return (
    <LinkProvider component={AppLink}>
      <Outlet />
    </LinkProvider>
  );
}

/** Public marketing pages. */
export function MarketingLayout() {
  return (
    <div className="page page--marketing">
      <SiteHeader />
      <main>
        <Outlet />
      </main>
      <SiteFooter />
    </div>
  );
}

const DASH_LINKS: Array<{ to: string; label: string; end?: boolean }> = [
  { to: '/app', label: 'Overview', end: true },
  { to: '/app/keys', label: 'API keys' },
  { to: '/app/usage', label: 'Usage' },
  { to: '/app/playground', label: 'Playground' },
  { to: '/app/billing', label: 'Plan & billing' },
];

/** Authenticated dashboard shell; redirects out if there is no session. */
export function DashboardLayout() {
  const auth = useStore($auth);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  useEffect(() => {
    if (auth.ready && !auth.user) {
      void navigate(`/login?next=${encodeURIComponent(pathname)}`, { replace: true });
    }
  }, [auth.ready, auth.user, navigate, pathname]);

  if (!auth.ready) {
    return (
      <div className="page">
        <div className="center-screen">
          <Spinner size="lg" label="Loading your account" />
        </div>
      </div>
    );
  }
  if (!auth.user) {
    // The redirect above is in flight; render nothing rather than a flash of UI.
    return null;
  }

  const links = auth.user.is_admin ? [...DASH_LINKS, { to: '/app/admin', label: 'Admin' }] : DASH_LINKS;

  return (
    <div className="page">
      <AppHeader />
      <main className="shell dash">
        <nav className="dash__nav" aria-label="Dashboard">
          {links.map((l) => (
            <NavLink key={l.to} to={l.to} end={l.end} className="dash__link">
              {l.label}
            </NavLink>
          ))}
        </nav>
        <div>
          {!auth.user.email_verified && <VerificationBanner />}
          <Outlet />
        </div>
      </main>
    </div>
  );
}

/**
 * Reminder shown across the dashboard until the account confirms its email.
 * Login stays open, but creating a key, running a forecast and subscribing
 * are all rejected server-side (email_not_verified) until this is done.
 */
function VerificationBanner() {
  const [state, setState] = useState<'idle' | 'pending' | 'sent' | 'error'>('idle');

  async function onResend() {
    setState('pending');
    try {
      await api.resendVerification();
      setState('sent');
    } catch {
      setState('error');
    }
  }

  return (
    <Banner
      status="warning"
      title="Confirm your email"
      description={
        state === 'sent'
          ? 'Verification email sent — check your inbox.'
          : 'Check your inbox to confirm your email address. Creating API keys, running forecasts and subscribing are disabled until then.'
      }
      endContent={
        state !== 'sent' ? (
          <Button
            label={state === 'error' ? 'Try again' : 'Resend email'}
            size="sm"
            variant="secondary"
            onClick={onResend}
            isLoading={state === 'pending'}
          />
        ) : undefined
      }
    />
  );
}

/** Login / register pages: minimal chrome, centred card. */
export function AuthLayout() {
  const auth = useStore($auth);
  const navigate = useNavigate();

  useEffect(() => {
    if (auth.ready && auth.user) void navigate('/app', { replace: true });
  }, [auth.ready, auth.user, navigate]);

  return (
    <div className="page page--marketing">
      <SiteHeader />
      <main className="shell shell--narrow auth-wrap">
        <Center width="100%">
          <Outlet />
        </Center>
      </main>
      <SiteFooter />
    </div>
  );
}

/**
 * Same minimal chrome as AuthLayout, but no redirect for a logged-in visitor —
 * the verify-email and cancel-signup links can be opened from a browser that
 * either does or doesn't hold the session that created the account, and
 * either way the page needs to run and show its own result.
 */
export function AuthLinkLayout() {
  return (
    <div className="page">
      <SiteHeader />
      <main className="shell shell--narrow auth-wrap">
        <Center width="100%">
          <Outlet />
        </Center>
      </main>
      <SiteFooter />
    </div>
  );
}
